import { Controller } from "@hotwired/stimulus"

// Remembers where a list was left — its URL with filter, sort and page — so a detail page's back
// link ("‹ Mitarbeiter") returns to that state instead of the bare, unfiltered list.
//
// For the tables whose filter lives only in the URL (Mitarbeiter-, Fuhrpark-Tabelle). The
// browser's back button needs none of this — the table's frame keeps the address bar in sync —
// but the back link must also work after the user clicked around on the detail page.
//
// Kept in sessionStorage rather than the Rails session: that is a cookie shared by every tenant
// subdomain and close to its 4 KB limit, while sessionStorage is per origin (= per tenant), per
// tab, and never sent to the server.
//
// Two roles, told apart by the `url` value:
//
//   On the list (rendered with every frame response, so it follows each search and page):
//   <div hidden data-controller="list-return" data-list-return-key-value="/employees"
//        data-list-return-url-value="/employees?page=2&view=table"></div>
//   An empty url (or the bare list) forgets — the list was left in a view the URL does not
//   describe (the Verzeichnis instead of the Tabelle).
//
//   On the back link, whose server-rendered href is the bare list:
//   <a href="/employees" data-controller="list-return" data-list-return-key-value="/employees">
const STORAGE_PREFIX = "list-return:"

export default class extends Controller {
  static values = { key: String, url: String }

  connect() {
    if (this.hasUrlValue) {
      this.remember()
    } else {
      this.applyToLink()
    }
  }

  remember() {
    try {
      if (this.urlValue.startsWith(`${this.keyValue}?`)) {
        window.sessionStorage.setItem(this.storageKey, this.urlValue)
      } else {
        window.sessionStorage.removeItem(this.storageKey)
      }
    } catch {
      // Storage blocked or full: the back link simply leads to the bare list.
    }
  }

  // Only a URL of the list itself is used: whatever the storage holds, the link never leads
  // anywhere but the list.
  applyToLink() {
    let url
    try {
      url = window.sessionStorage.getItem(this.storageKey)
    } catch {
      return
    }
    if (url?.startsWith(`${this.keyValue}?`)) this.element.setAttribute("href", url)
  }

  get storageKey() {
    return `${STORAGE_PREFIX}${this.keyValue}`
  }
}
